// HTTP API para notificacoes. Wrapper fino sobre `api` (ky), sem React.
// Hooks TanStack vivem em `notifications.queries.ts`.
//
// Backend doc canonico: `docs/api/notifications.md`.
//
// Endpoints cobertos:
//   GET    /notifications                  -> PaginatedResponse<Notification>
//   PATCH  /notifications/:id/read         -> ApiResponse<Notification>
//   PATCH  /notifications/read-all         -> ApiResponse<{ marked: number }>
//   GET    /notifications/preferences      -> ApiResponse<NotificationPreferences>
//   PATCH  /notifications/preferences      -> ApiResponse<NotificationPreferences>
//
// Cada response passa por Zod parse no boundary — se o backend mudar o
// shape, falhamos cedo com erro tipado em vez de propagar undefined em
// renderers.

import {
  markReadAllResponseSchema,
  notificationPreferencesSchema,
  notificationSchema,
} from '../schemas/notification.schema';
import type {
  MarkReadAllResponse,
  Notification,
  NotificationListFilters,
  NotificationPreferences,
  UpdatePreferencesRequest,
} from '../types/notification.types';

import { api } from '@/shared/api/client';
import type { ApiResponse, PaginatedResponse } from '@/shared/api/client.types';
import { logger } from '@/shared/lib/logger';

function buildSearchParams(filters: NotificationListFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.page != null) out.page = String(filters.page);
  if (filters.per_page != null) out.per_page = String(filters.per_page);
  if (filters.sort_order) out.sort_order = filters.sort_order;
  if (filters.type) out.type = filters.type;
  if (filters.is_read !== undefined) out.is_read = String(filters.is_read);
  if (filters.start_date) out.start_date = filters.start_date;
  if (filters.end_date) out.end_date = filters.end_date;
  if (filters.search) out.search = filters.search;
  return out;
}

export const notificationsApi = {
  list: async (filters: NotificationListFilters = {}): Promise<PaginatedResponse<Notification>> => {
    const res = await api
      .get('notifications', { searchParams: buildSearchParams(filters) })
      .json<PaginatedResponse<unknown>>();
    // Parse resiliente: um único item com shape inesperado NÃO derruba a
    // central inteira (o `.parse()` antigo lançava e quebrava lista + sino).
    // Itens inválidos são pulados e logados para investigação.
    const data: Notification[] = [];
    for (const item of res.data) {
      const parsed = notificationSchema.safeParse(item);
      if (parsed.success) {
        data.push(parsed.data);
      } else {
        logger.api.warn('Notificação ignorada (shape inesperado):', parsed.error.issues);
      }
    }
    return { ...res, data };
  },

  markAsRead: async (id: string): Promise<ApiResponse<Notification>> => {
    const res = await api.patch(`notifications/${id}/read`).json<ApiResponse<unknown>>();
    return { success: true, data: notificationSchema.parse(res.data) };
  },

  markAllAsRead: async (): Promise<ApiResponse<MarkReadAllResponse>> => {
    const res = await api.patch('notifications/read-all').json<ApiResponse<unknown>>();
    return { success: true, data: markReadAllResponseSchema.parse(res.data) };
  },

  getPreferences: async (): Promise<ApiResponse<NotificationPreferences>> => {
    const res = await api.get('notifications/preferences').json<ApiResponse<unknown>>();
    return { success: true, data: notificationPreferencesSchema.parse(res.data) };
  },

  updatePreferences: async (
    body: UpdatePreferencesRequest,
  ): Promise<ApiResponse<NotificationPreferences>> => {
    const res = await api
      .patch('notifications/preferences', { json: body })
      .json<ApiResponse<unknown>>();
    return { success: true, data: notificationPreferencesSchema.parse(res.data) };
  },
};
